import type { ID } from '@octopus-do/octopus-project-core'
import { useMemo } from 'react'

import type { StructureNode, StructureSection } from '../types'

const filterNode = (
  node: StructureNode,
  matchIds: Set<ID>
): StructureNode | null => {
  const children = node.children
    .map((child) => filterNode(child, matchIds))
    .filter((child): child is StructureNode => child !== null)

  if (matchIds.has(node.id) || children.length) {
    return { ...node, children }
  }

  return null
}

// An empty query leaves the tree untouched. A non-empty one keeps the matching
// pages plus the ancestors that lead to them, so a match is never orphaned from
// its place in the sitemap.
//
// Sections that lose every page stay in the list as empty ones: dropping them
// would promote a later section into the heading-less "first section" slot and
// silently strip its title off the results.
export const useFilteredStructure = (
  sections: StructureSection[],
  query: string,
  pageIds: Set<ID>
): StructureSection[] =>
  useMemo(() => {
    if (!query) {
      return sections
    }

    return sections.map((section) => ({
      ...section,
      roots: section.roots
        .map((root) => filterNode(root, pageIds))
        .filter((root): root is StructureNode => root !== null),
    }))
  }, [sections, query, pageIds])
